// filepath: src/components/Features.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import { 
    Container, 
    Box, 
    Grid, 
    Typography, 
    Paper, 
    Button,
    useTheme,
    useMediaQuery,
    styled
} from "@mui/material";
import { 
    Speed, 
    SettingsBackupRestore, 
    AccessTime, 
    Dashboard, 
    History, 
    Security 
} from "@mui/icons-material";
import { motion } from "framer-motion";

// --- Framer Motion Components ---
const MotionPaper = styled(motion(Paper))({});
const MotionButton = motion(Button);

// --- ANIMATION VARIANTS ---

// Header entrance variant
const headerEnter = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

// Item entrance variant (used for features and CTA elements)
const itemEnter = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1, 
        transition: { 
            duration: 0.5, 
            ease: [0.4, 0, 0.2, 1] // Fast start, smooth end
        } 
    },
};

// Container stagger for the main grid (triggered by scroll/whileInView)
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
        staggerChildren: 0.15, 
        delayChildren: 0.2 
    } 
  },
};

// Icon mapping using Material-UI Icons
const FeatureIcons = [
    <Speed />, // Live Resource Metrics
    <SettingsBackupRestore />, // Auto-Scaling & Recommendations
    <AccessTime />, // Alert & Notification System
    <Dashboard />, // Comprehensive Dashboard
    <History />, // Historical Logs & Replay
    <Security /> // Security & Access Control
];


const Features = () => {
  const { t } = useTranslation();
  const featuresList = t("features.list", { returnObjects: true });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        background: "linear-gradient(135deg, #f3f4f6, #e0e7ff)",
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="lg" sx={{ 
          py: { xs: 8, md: 12 } // Responsive padding
        }}>
        {/* Header */}
        <motion.div variants={headerEnter} initial="hidden" animate="visible">
          <Box textAlign="center" mb={{ xs: 6, md: 10 }}>
            <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold" gutterBottom>
              {t("features.header.title")}
            </Typography>
            <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" maxWidth={800} mx="auto">
              {t("features.header.subtitle")}
            </Typography>
          </Box>
        </motion.div>

        {/* Features Grid - Scroll Reveal Orchestration */}
        <motion.div 
            variants={staggerContainer} 
            initial="hidden" 
            whileInView="visible" // Animation triggers when section scrolls into view
            viewport={{ once: true, amount: isTablet ? 0.1 : 0.3 }} // More sensitive trigger on mobile
        >
          <Grid container spacing={{ xs: 4, md: 5 }}>
            {featuresList.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <motion.div variants={itemEnter}>
                  <MotionPaper // Use MotionPaper for Framer Motion effects
                    elevation={5}
                    // Advanced Framer Motion Hover Effects
                    whileHover={{ 
                        scale: 1.03, 
                        boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)", 
                        y: -5 // Subtle lift
                    }}
                    whileTap={{ scale: 0.98 }}
                    sx={{
                      p: { xs: 3, md: 4 },
                      borderRadius: 3,
                      textAlign: "left", // Align content left for better mobile reading
                      minHeight: isMobile ? 'auto' : 240, // Uniform height on desktop
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 56,
                        height: 56,
                        bgcolor: theme.palette.primary.light, // Use a lighter primary color for background
                        borderRadius: "50%", // Circular icon background
                        mb: 2,
                        color: theme.palette.primary.contrastText, // Icon color (usually white)
                      }}
                    >
                      {React.cloneElement(FeatureIcons[index % FeatureIcons.length], { style: { fontSize: 32 } })}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography color="text.secondary">
                        {feature.description}
                    </Typography>
                  </MotionPaper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* CTA Section - Scroll Reveal Orchestration */}
        <motion.div 
            variants={itemEnter} // Simple item entrance for the whole block
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true, amount: 0.5 }}
        >
          <Box 
            textAlign="center" 
            mt={{ xs: 10, md: 12 }}
            sx={{
                bgcolor: 'primary.main',
                color: 'white',
                p: { xs: 4, md: 6 },
                borderRadius: 3,
            }}
          >
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
              {t("features.cta.title")}
            </Typography>
            <Typography variant={isMobile ? "body1" : "h6"} color="#e0e7ff" mb={4}>
              {t("features.cta.subtitle")}
            </Typography>
            <MotionButton
                variant="contained" 
                color="secondary" 
                size="large"
                // Framer Motion micro-interaction
                whileHover={{ scale: 1.05, boxShadow: "0 8px 15px rgba(255, 255, 255, 0.3)" }}
                whileTap={{ scale: 0.95 }}
            >
              {t("features.cta.button")}
            </MotionButton>
          </Box>
        </motion.div>
      </Container>
    </motion.div>
  );
};

export default Features;
